import { useRecoilState, useRecoilValue } from 'recoil'
import userAtom from '@/pages/User/user.atom.ts'
import { useMemo, useState } from 'react'
import analyticsConstants from '@/pages/Equipment/pages/LiftPage/pages/LiftAnalysis/LiftAnalysisContent/analytics.constants.tsx'
import { IUserAnalyticsPreset } from '@/pages/Users/users.types.ts'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import analyticsUtils from '@/pages/Equipment/pages/LiftPage/pages/LiftAnalysis/LiftAnalysisContent/analytics.utils.ts'
import userApi from '@/pages/User/user.api.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import { defaultColorOptions } from '@/shared/Inputs/ColorDropdown'
import parametersSelectors from '@/pages/Parameters/parameters.selectors.ts'
import { ITab } from '@/shared/Tabs/tabs.types.ts'
import ReplicateIcon from '@/assets/icons/replicateButton.svg?react'
import RemoveIcon from '@/assets/icons/close.svg?react'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import objectUtils from '@/utils/object.utils.ts'

const useAnalyticsPresetsCtrl = (protocolId?: string) => {
  const { wait, loading } = useHttpLoader()
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const sourceIdToIdMap = useRecoilValue(parametersSelectors.getSourceIdToIdMap)

  const [userState, setUserState] = useRecoilState(userAtom)
  const userSettings = userState.settings

  const defaultPresets = useMemo(() => analyticsConstants.getPresets(protocolId), [protocolId])

  const [selectedPresetId, setSelectedPresetId] = useState<string>(defaultPresets[0]?.id ?? '')
  const [presets, setPresets] = useState<IUserAnalyticsPreset[]>([
    ...defaultPresets,
    ...userSettings.liftMonitoringParameterPresets.filter((p) => !defaultPresets.some((dp) => dp.id === p.id)),
  ])

  const selectedPreset = useMemo(() => {
    return presets.find((p) => p.id === selectedPresetId)
  }, [presets, selectedPresetId])

  const handlePresetCreate = async (fromPreset: IUserAnalyticsPreset = null) => {
    const generated = analyticsUtils.generateEmptyPreset(presets)

    const newPreset = fromPreset ? { ...fromPreset, id: generated.id, title: generated.title } : generated

    await wait(
      userApi.updateSelfSettings({
        ...userSettings,
        liftMonitoringParameterPresets: [...userSettings.liftMonitoringParameterPresets, newPreset],
      }),
      (resp) => {
        if (resp.status === 'success') {
          setUserState((prev) => ({
            ...prev,
            settings: resp.body,
          }))
          setPresets([...defaultPresets, ...resp.body.liftMonitoringParameterPresets])
          setSelectedPresetId(newPreset.id)
        }
      }
    )
  }

  const handleSelectedPresetAddParameter = (parameterId: IParameter['id']) => {
    const parameterSourceId = parametersMap[parameterId]?.sourceId
    if (!parameterSourceId) return

    const updatedPreset: IUserAnalyticsPreset = {
      ...selectedPreset,
      parameters: [
        ...selectedPreset.parameters,
        {
          sourceId: parameterSourceId,
          color: defaultColorOptions[(selectedPreset.parameters.length + 1) % defaultColorOptions.length].value,
        },
      ],
    }

    const index = userSettings.liftMonitoringParameterPresets.findIndex((it) => it.id === updatedPreset.id)
    if (index === -1) return

    const updatedPresets = [
      ...userSettings.liftMonitoringParameterPresets.slice(0, index),
      updatedPreset,
      ...userSettings.liftMonitoringParameterPresets.slice(index + 1),
    ]

    // Работаем оптимистично: заранее добавляем параметр в стейт и убираем его туда только в случае неуспешного запроса на обновление
    const prevPresets = objectUtils.deepCopy(presets)
    setPresets([...defaultPresets, ...updatedPresets])

    wait(
      userApi.updateSelfSettings({
        ...userSettings,
        liftMonitoringParameterPresets: updatedPresets,
      }),
      (resp) => {
        if (resp.status === 'success') {
          setUserState((prev) => ({
            ...prev,
            settings: resp.body,
          }))

          setPresets([...defaultPresets, ...resp.body.liftMonitoringParameterPresets])
        } else {
          setPresets(prevPresets)
        }
      }
    )
  }

  const handleSelectedPresetRemoveParameter = (parameterId: IParameter['id']) => {
    const parameterSourceId = parametersMap[parameterId]?.sourceId
    if (!parameterSourceId) return

    const updatedPreset: IUserAnalyticsPreset = {
      ...selectedPreset,
      parameters: selectedPreset.parameters.filter((p) => p.sourceId !== parameterSourceId),
    }

    const index = userSettings.liftMonitoringParameterPresets.findIndex((it) => it.id === updatedPreset.id)
    if (index === -1) return

    const updatedPresets = [
      ...userSettings.liftMonitoringParameterPresets.slice(0, index),
      updatedPreset,
      ...userSettings.liftMonitoringParameterPresets.slice(index + 1),
    ]

    // Работаем оптимистично: заранее удаляем параметр из стейта и возвращаем его туда только в случае неуспешного запроса на обновление
    const prevPresets = objectUtils.deepCopy(presets)
    setPresets([...defaultPresets, ...updatedPresets])

    wait(
      userApi.updateSelfSettings({
        ...userSettings,
        liftMonitoringParameterPresets: updatedPresets,
      }),
      (resp) => {
        if (resp.status === 'success') {
          setUserState((prev) => ({
            ...prev,
            settings: resp.body,
          }))

          setPresets([...defaultPresets, ...resp.body.liftMonitoringParameterPresets])
        } else {
          setPresets(prevPresets)
        }
      }
    )
  }

  const handleSelectedPresetNameUpdate = (name: string) => {
    const updatedPreset: IUserAnalyticsPreset = {
      ...selectedPreset,
      title: name,
    }

    const index = userSettings.liftMonitoringParameterPresets.findIndex((it) => it.id === updatedPreset.id)
    if (index === -1) return

    wait(
      userApi.updateSelfSettings({
        ...userSettings,
        liftMonitoringParameterPresets: [
          ...userSettings.liftMonitoringParameterPresets.slice(0, index),
          updatedPreset,
          ...userSettings.liftMonitoringParameterPresets.slice(index + 1),
        ],
      }),
      (resp) => {
        if (resp.status === 'success') {
          setUserState((prev) => ({
            ...prev,
            settings: resp.body,
          }))

          setPresets([...defaultPresets, ...resp.body.liftMonitoringParameterPresets])
        }
      }
    )
  }

  const handleRemovePreset = (id: IUserAnalyticsPreset['id']) => {
    const removingPreset = presets.find((it) => it.id === id)
    if (!removingPreset) return

    confirmService.requestConfirm({
      title: 'Подтвердите удаление представления',
      description: `Представление "${removingPreset.title}" будет удалено без возможности восстановления.`,
      isDanger: true,
      onConfirm: async () => {
        await wait(
          userApi.updateSelfSettings({
            ...userSettings,
            liftMonitoringParameterPresets: userSettings.liftMonitoringParameterPresets.filter((p) => p.id !== id),
          }),
          (resp) => {
            if (resp.status === 'success') {
              setSelectedPresetId((prev) => {
                if (prev === id) {
                  return defaultPresets[0].id
                }

                return prev
              })

              setUserState((prev) => ({
                ...prev,
                settings: resp.body,
              }))
              setPresets([...defaultPresets, ...resp.body.liftMonitoringParameterPresets])
            }
          }
        )
      },
    })
  }

  const selectedPresetParametersConfig: Array<{ parameterId: IParameter['id']; color: string }> = useMemo(() => {
    if (!selectedPreset) return []

    return selectedPreset.parameters.map((p) => ({ parameterId: sourceIdToIdMap[p.sourceId], color: p.color }))
  }, [selectedPreset])

  const tabs: ITab[] = useMemo(() => {
    return presets.map((p) => ({
      title: p.title,
      id: p.id,
      actionIcon: p.isLocked ? <ReplicateIcon /> : <RemoveIcon />,
      onActionClick: p.isLocked ? () => handlePresetCreate(p) : () => handleRemovePreset(p.id),
    }))
  }, [presets])

  return {
    data: {
      presets,
      selectedPreset,
      loading,
      selectedPresetParametersConfig,
      tabs,
    },
    handlers: {
      handlePresetCreate,
      handleSelectPreset: setSelectedPresetId,
      handleSelectedPresetAddParameter,
      handleSelectedPresetRemoveParameter,
      handleSelectedPresetNameUpdate,
    },
  }
}

export default useAnalyticsPresetsCtrl
